import { BaseStore } from "@omni/types/enums/baseStore.enum";
import { Environment } from "@omni/types/enums/environment.enum";
import { DeepPartial } from "@omni/types/utils/deepPartial";
import { ModelMapper } from "@omni/types/utils/modelMapper";
import { values } from "@omni/utils/object.utils";
import * as yup from "yup";

import { yupBaseStoreAware } from "../common/baseStoreAware.schema";

export type RawSsrConfig = {
    APPLICATION_NAME: string;
    ENV: Environment;
    BFF_HOST: string;
    HUBO_ASSETS_HOST: string;
    ETNA_ASSETS_HOST: string;
    HUBO_FONTS: string;
    ETNA_FONTS: string;
    CACHE_TTL: number;
    HUBO_AEM_HOST: string;
    HUBO_AEM_AUTHOR_HOST: string;
    HUBO_COMPONENTS_VERSION: string | undefined;
    HUBO_COMPONENTS_HOST: string;
    HUBO_REFERER: string;
    ETNA_AEM_HOST: string;
    ETNA_AEM_AUTHOR_HOST: string;
    ETNA_COMPONENTS_HOST: string;
    ETNA_COMPONENTS_VERSION: string | undefined;
    ETNA_REFERER: string;
};

const toSsrConfig: ModelMapper<
    DeepPartial<RawSsrConfig>,
    DeepPartial<SsrConfig>
> = (config) => {
    return {
        applicationName: config.APPLICATION_NAME,
        environment: config.ENV,
        aemHost: {
            [BaseStore.Hubo]: config.HUBO_AEM_HOST,
            [BaseStore.Etna]: config.ETNA_AEM_HOST
        },
        aemAuthorHost: {
            [BaseStore.Hubo]: config.HUBO_AEM_AUTHOR_HOST,
            [BaseStore.Etna]: config.ETNA_AEM_AUTHOR_HOST
        },
        bffHost: config.BFF_HOST,
        assetsHost: {
            [BaseStore.Hubo]: config.HUBO_ASSETS_HOST,
            [BaseStore.Etna]: config.ETNA_ASSETS_HOST
        },
        fonts: {
            [BaseStore.Hubo]:
                config.HUBO_FONTS?.split(",").map((font) => font.trim()) || [],
            [BaseStore.Etna]:
                config.ETNA_FONTS?.split(",").map((font) => font.trim()) || []
        },
        componentsHost: {
            [BaseStore.Hubo]: config.HUBO_COMPONENTS_HOST,
            [BaseStore.Etna]: config.ETNA_COMPONENTS_HOST
        },
        componentVersions: {
            [BaseStore.Hubo]: config.HUBO_COMPONENTS_VERSION || "",
            [BaseStore.Etna]: config.ETNA_COMPONENTS_VERSION || ""
        },
        referer: {
            [BaseStore.Hubo]: config.HUBO_REFERER,
            [BaseStore.Etna]: config.ETNA_REFERER
        },
        cache: {
            ttl: config.CACHE_TTL
        }
    };
};

export const SsrConfigSchema = yup
    .object()
    .transform((_, rawConfig: DeepPartial<RawSsrConfig>) => {
        return toSsrConfig(rawConfig);
    })
    .shape({
        applicationName: yup.string().required(),
        environment: yup
            .mixed<Environment>()
            .oneOf(values(Environment))
            .required(),
        aemHost: yupBaseStoreAware(yup.string().required()),
        aemAuthorHost: yupBaseStoreAware(yup.string().required()),
        bffHost: yup.string().required(),
        assetsHost: yupBaseStoreAware(yup.string().required()),
        componentsHost: yupBaseStoreAware(yup.string().required()),
        fonts: yupBaseStoreAware(
            yup.array().of(yup.string().required()).required()
        ),
        componentVersions: yupBaseStoreAware(
            yup
                .string()
                .when("environment", {
                    is: (env: Environment) =>
                        [
                            Environment.Test,
                            Environment.Acpt,
                            Environment.Prod
                        ].includes(env),
                    then: (schema) => schema.required(),
                    otherwise: (schema) => schema.notRequired().default("")
                })
                .required()
        ),
        referer: yupBaseStoreAware(yup.string().required()),
        cache: yup.object({
            ttl: yup.number().required()
        })
    });

export type SsrConfig = yup.InferType<typeof SsrConfigSchema>;
